const SearchParameters = require("./searchparameters").SearchParameters;
const SearchQuery = require("./searchquery").SearchQuery;
const network = require("./network");

/**
 * @memberOf woosmap
 */
class DataSource {
    /**
     * The DataSource object is the bridge to the server API.
     * @param {String} [api_url]
     * @param {String} [api_key]
     * @constructor
     */
    constructor(api_url, api_key) {
        this.api_url = api_url || woosmap.api_url;
        this.api_key = api_key || woosmap.public_key;
    }

    /**
     * Used to build query urls
     * @private
     * @param {String}view api view to get data.
     * @param {woosmap.search.SearchParameters} [searchParameters]
     */
    _buildUrlQuery(view, searchParameters) {
        let url = `${this.api_url}/stores/${view}?key=${this.api_key}`;
        if (searchParameters) {
            url += searchParameters.buildUrl();
        }
        return url;
    }

    /**
     * Make an ajax call with the url and call the callback if success
     * @param {String} url url for the request
     * @param {Function} callback callback called if request is successful
     */
    getFromApi(url, callback) {
        const self = this;

        function requestCallback(data) {
            if (url.indexOf("page") >= 0) {
                callback(data);
            } else {
                self._requestAllPagesCallback(data, { features: [], type: "FeatureCollection" }, 1, url, callback);
            }
        }

        return this._getFromApiDeferred(url).then(requestCallback);
    }

    /**
     * Call the expected callback after retrieving all the data from all the requested page
     * @param {Object} requestedData the data given by the previous request
     * @param {Object} completedData the data which will be completed call by call
     * @param {Number} currentLoadedPage the requested page to get `requestedData`
     * @param {String} url the global url to request (without the page number)
     * @param {function} callback the final callback to call when all the page will be loaded
     * @private
     */
    _requestAllPagesCallback(requestedData, completedData, currentLoadedPage, url, callback) {
        completedData.features = completedData.features.concat(requestedData.features);
        if (requestedData.pagination.pageCount && requestedData.pagination.pageCount > currentLoadedPage) {
            currentLoadedPage += 1;
            this._getFromApiDeferred(this._updateUrlWithPaginationOptions(url, currentLoadedPage)).then((data) => {
                this._requestAllPagesCallback(data, completedData, currentLoadedPage, url, callback);
            });
        } else {
            callback(completedData);
        }
    }

    /**
     * Complete the request url with the page number
     * @param {String} url the url for the request
     * @param {Number} selectedPage the page to retrieve
     * @returns {string} the completed url
     * @private
     */
    _updateUrlWithPaginationOptions(url, selectedPage) {
        const argPrefix = url.indexOf("?") >= 0 ? "&" : "?";
        return `${url + argPrefix}page=${selectedPage}`;
    }

    /**
     * Gets a Deferred for an async get
     * @param url
     * @returns {Promise}
     * @private
     */
    _getFromApiDeferred(url) {
        return network.fetch(url).then((response) => {
            return response.json();
        });
    }

    /**
     * Returns all the stores.
     * @param {Function} callback
     * @returns {Promise}
     */
    getAllStores(callback) {
        return this.getFromApi(this._buildUrlQuery(""), callback);
    }

    /**
     * Fetches stores that are near the (lng, lat) position and pass it to the callback.
     * if `lat` and `lng` are not defined, we use the IP geolocation to pinpoint the user.
     * @param {Number} lat the latitude.
     * @param {Number} lng the longitude.
     * @param {function} callback the callback to be called with the stores.
     * @param {Number} maxStores the maximum number of stores returned.
     * @param {Number} maxDistanceFromUser radius in meters to filter the stores.
     * @returns {Promise}
     */
    getNearbyStores(lat, lng, callback, maxStores, maxDistanceFromUser) {
        const paramsOptions = {
            storesByPage: maxStores || 1,
            page: 1,
        };

        if (lat !== undefined || lng !== undefined) {
            paramsOptions.lat = lat;
            paramsOptions.lng = lng;
        }

        if (maxDistanceFromUser) {
            paramsOptions.maxDistanceFromUser = maxDistanceFromUser;
        }
        const searchParameters = new SearchParameters(paramsOptions);
        return this.getFromApi(this._buildUrlQuery("nearby", searchParameters), callback);
    }

    /**
     * Fetches a store by its store_id.
     * @param {String} store_id
     * @param {Function} callback
     * @returns {Promise}
     */
    getStoreById(store_id, callback) {
        const url = this._buildUrlQuery(store_id);
        return this._getFromApiDeferred(url).then(callback);
    }

    /**
     * Fetches stores that name matches the search query and pass it to the callback.
     * @param {string}query the query
     * @param {function}callback the callback to be called with the stores.
     * @returns {Promise}
     * @deprecated replaced by searchStoresByParameters
     * @see woosmap.DataSource#searchStores
     */
    getStoresBySearchQuery(query, callback) {
        const searchParameters = new SearchParameters({
            querySearchOptions: { query: { value: query, options: { name: SearchQuery.AND } } },
        });
        return this.searchStoresByParameters(searchParameters, callback);
    }

    /**
     * Fetches stores that matches the search query and pass it to the callback.
     * @param {woosmap.search.SearchQuery}searchQuery the query object (composed by query, tags and types)
     * @param {function}callback the callback to be called with the stores.
     * @returns {Promise}
     * @deprecated use searchStoresByParameters
     */
    searchStores(searchQuery, callback) {
        const searchParameters = new SearchParameters({ querySearchOptions: searchQuery });
        return this.searchStoresByParameters(searchParameters, callback);
    }

    /**
     * Fetches nearby stores that matches the search query and pass it to the callback.
     * @param {woosmap.search.SearchQuery}searchQuery the query object (composed by query, tags and types)
     * @param {Number}lat the latitude.
     * @param {Number}lng the longitude.
     * @param {Function} callback the callback to be called with the stores.
     * @param {Number} maxStores the maximum number of stores returned.
     * @param {Number} maxDistanceFromUser radius in meters to filter the stores.
     * @returns {Promise}
     * @deprecated use searchStoresByParameters
     */
    searchNearbyStores(searchQuery, lat, lng, callback, maxStores, maxDistanceFromUser) {
        const paramsOptions = {
            storesByPage: maxStores || 1,
            querySearchOptions: searchQuery,
            lat: lat,
            lng: lng,
            maxDistanceFromUser: maxDistanceFromUser,
            page: 1,
        };
        const searchParameters = new SearchParameters(paramsOptions);
        return this.searchStoresByParameters(searchParameters, callback);
    }

    /**
     * Search on store given a list of information
     * @param {woosmap.search.SearchParameters} searchParameters an object containing all the data to do the search.
     * @param {Function} callback the callback to be called with the stores.
     * @returns {Promise}
     */
    searchStoresByParameters(searchParameters, callback) {
        return this.getFromApi(this._buildUrlQuery("search", searchParameters), callback);
    }

    /**
     * return the number of stores matching parameters
     * @param {woosmap.search.SearchParameters} searchParameters an object containing all the data to do the search.
     * @param {Function} callback the callback to be called with the stores count as an Object.
     * @returns {Promise}
     */
    countStoresByParameters(searchParameters, callback) {
        return this._getFromApiDeferred(this._buildUrlQuery("search/count", searchParameters)).then(callback);
    }

    /**
     * return the bounds for all stores matching parameters
     * @param {woosmap.search.SearchParameters} searchParameters an object containing all the data to do the search.
     * @param {Function} callback the callback to be called with the bounds as google.maps.LatLngBoundsLiteral.
     * returns {} if no stores matched.
     * @returns {Promise}
     */
    storesBoundsByParameters(searchParameters, callback) {
        return this._getFromApiDeferred(this._buildUrlQuery("search/bounds", searchParameters)).then(callback);
    }

    /**
     * Get the config object for the Project.
     * @param {Function} callback the callback to be called with the config.
     * @returns {Promise}
     */
    getProjectConfig(callback) {
        return this._getProjectConfig().then(callback);
    }

    /**
     * Gets the config object for the Project.
     * @returns {Promise}
     * @private
     */
    _getProjectConfig() {
        return this._getFromApiDeferred(`${this.api_url}/project/config?key=${this.api_key}`);
    }
}

exports.DataSource = DataSource;

exports.getProjectConfig = (callback) => {
    if (window.woosmap) {
        if (window.woosmap.config?.updated) {
            callback(window.woosmap.config);
        } else {
            new DataSource()
                ._getProjectConfig()
                .then((config) => {
                    window.woosmap.config = config;
                    callback(config);
                })
                .catch((e) => {
                    if (console?.error) {
                        console.error("DataSource Unable to get ProjectConfig");
                        console.error(e);
                    }
                    callback({
                        config: new Date().getTime(),
                    });
                });
        }
    }
};
